import { useCallback, useRef, useState } from "react";
import { classNames } from "primereact/utils";
import { AppDialog } from "../../../AppDialog/AppDialog";
import { Icon, IconType } from "../../../../icons/Icon";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { useBarcodeScanner } from "./hooks/useBarcodeScanner";
import styles from "./BarcodeScannerModal.module.scss";

interface IBarcodeScannerModalProps {
  visible: boolean;
  onHide: () => void;
  onScanned: (value: string) => void;
}

/**
 * Full scanner dialog. Live camera path is the default; when
 * `getUserMedia` is unsupported (or fails with a non-permission error)
 * the dialog swaps to a file-input backed by the OS camera app.
 *
 * Permission denial closes the dialog immediately and surfaces a toast
 * via the hook — we always re-request permission on the next open.
 */
export const BarcodeScannerModal = ({
  visible,
  onHide,
  onScanned,
}: IBarcodeScannerModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFlashing, setIsFlashing] = useState(false);

  const handleSuccess = useCallback(
    (value: string) => {
      setIsFlashing(true);
      window.setTimeout(() => {
        setIsFlashing(false);
        onScanned(value);
      }, 250);
    },
    [onScanned]
  );

  const handlePermissionDenied = useCallback(() => {
    // Toast is surfaced by the caller, which also closes us. We still
    // want the dialog closed cleanly here in case we get re-entered.
    onHide();
  }, [onHide]);

  const { videoRef, state, errorMessage, decodeFromFile } = useBarcodeScanner({
    enabled: visible,
    onScanned: handleSuccess,
    onPermissionDenied: handlePermissionDenied,
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so the same photo can be re-picked after a failure.
    event.target.value = "";
    if (!file) return;
    void decodeFromFile(file);
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const showSpinner = state === "initializing" || state === "decoding";
  const showFallback = state === "fallback" || state === "error";

  return (
    <AppDialog
      header="Сканиране на баркод"
      headerIconType={IconType.Camera}
      visible={visible}
      onHide={onHide}
      tooltip="Насочете камерата към баркода или QR кода. Ако устройството не поддържа камерата директно, ще може да направите снимка."
    >
      <div className={styles.root}>
        <RenderWhen condition={!showFallback}>
          <div
            className={classNames(styles.viewport, {
              [styles.viewport_success]: isFlashing,
            })}
          >
            <video
              ref={videoRef}
              className={styles.video}
              muted
              playsInline
              autoPlay
            />
            <div className={styles.reticle} />
            <RenderWhen condition={showSpinner}>
              <div className={styles.loading}>
                <div className={styles.spinner} />
              </div>
            </RenderWhen>
            <RenderWhen condition={isFlashing}>
              <div className={styles.checkmark}>
                <div className={styles.checkmark_icon}>✓</div>
              </div>
            </RenderWhen>
          </div>
          <p className={styles.hint}>
            Насочете камерата към баркода или QR кода.
          </p>
        </RenderWhen>

        <RenderWhen condition={showFallback}>
          <div className={styles.fallback}>
            <div className={styles.fallback_icon}>
              <Icon type={IconType.Camera} width="42px" height="42px" />
            </div>
            <p className={styles.fallback_title}>
              Камерата не е достъпна директно
            </p>
            <p className={styles.fallback_subtitle}>
              Натиснете бутона по-долу, за да направите снимка на баркода с
              вашия телефон.
            </p>
            <RenderWhen condition={!!errorMessage}>
              <p className={styles.fallback_error}>{errorMessage}</p>
            </RenderWhen>
            <button
              type="button"
              className={styles.fallback_button}
              onClick={openFilePicker}
            >
              <Icon type={IconType.Camera} width="18px" height="18px" />
              Направи снимка
            </button>
          </div>
        </RenderWhen>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className={styles.hidden_input}
          onChange={handleFileChange}
        />
      </div>
    </AppDialog>
  );
};
